<template>
  <div v-if="showCookieComply" class="overlay-background vw-100 vh-100 position-fixed top-0 start-0 d-flex align-items-center">
    <aside class="cookie-comply">
      <div class="cookie-comply__header">
        <slot name="header">
          <h3 class="cookie-comply__header-title">{{ headerTitle[language] }}</h3>
          <p class="cookie-comply__header-description">
            <span v-html="headerDescription[language]" />
          </p>
        </slot>
      </div>

      <div class="cookie-comply__actions">
        <cookie-comply-button @handleClick="openPreferences">
          {{ preferencesLabel[language] }}
        </cookie-comply-button>
        <button
          class="btn btn-primary fw-semibold"
          @click="handleAcceptAll"
        >
          {{ acceptAllLabel[language] }}
        </button>
      </div>
    </aside>
  </div>

  <Teleport :to="target">
    <cookie-comply-modal
      v-if="isModalOpen"
      :language="language"
      :preferences="preferences"
      @cookie-comply-save="onSave"
      @cookie-comply-close="isModalOpen = false"
    >
      <template #modal-header>
        <!-- <slot name="modal-header"></slot> -->
      </template>

      <template #modal-body="{ preference, index }">
        <slot
          name="modal-body"
          :preference="preference"
          :index="index"
        ></slot>
      </template>

      <template #modal-footer>
        <slot name="modal-footer"></slot>
      </template>
    </cookie-comply-modal>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue'
import CookieComplyModal from './CookieComplyModal.vue'
import CookieComplyButton from './CookieComplyButton.vue'

// Definicje typu dla danych
interface CookieItem {
  label: string;
  value: string;
  expiration: string;
  domain: string;
  company: string;
  privacy: string;
}

interface Preference {
  title: {
    pl: string;
    en: string;
  };
  isRequired: boolean;
  isEnabled: boolean;
  value: {
    pl: string;
    en: string;
  };
  description: {
    pl: string;
    en: string;
  };
  items: {
    pl: CookieItem[];
    en: CookieItem[];
  };
}

interface Props {
  language: string,
  companyName: string,
  privacyPolicyUrl: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'on-accept-all-cookies'): void
  (e: 'on-save-cookie-preferences', data: any): void
}>()

// Zmienna przechowująca preferencje cookie
const preferences = ref<Preference[]>([
  {
    title: {
      pl: 'Niezbędne',
      en: 'Required'
    },
    isRequired: true,
    isEnabled: true,
    value: {
      pl: 'niezbedne',
      en: 'required'
    },
    description: {
      pl: 'Pliki cookies, które są potrzebne do prawidłowego działania strony WWW. Umożliwiają Tobie poprawne korzystanie ze strony oraz jej podstawowych funkcji.',
      en: 'Cookies are needed for the website to function properly. They enable you to use the website and its basic functions correctly.'
    },
    items: {
      en: [
        {
          label: 'XSRF-TOKEN',
          value: 'eyJpdiI6IlkrL0c3VXh1WFJySVhNbW...',
          expiration: '1 hour',
          domain: window.location.hostname,
          company: props.companyName,
          privacy: props.privacyPolicyUrl
        },
        {
          label: 'app-session-id',
          value: 'web1~eyJpdiI6IkxVeTJ0UUI2VUV5a...',
          expiration: '1 hour',
          domain: window.location.hostname,
          company: props.companyName,
          privacy: props.privacyPolicyUrl
        }
      ],
      pl: [
        {
          label: 'XSRF-TOKEN',
          value: 'eyJpdiI6IlkrL0c3VXh1WFJySVhNbW...',
          expiration: '1 godzina',
          domain: window.location.hostname,
          company: props.companyName,
          privacy: props.privacyPolicyUrl
        },
        {
          label: 'app-session-id',
          value: 'web1~eyJpdiI6IkxVeTJ0UUI2VUV5a...',
          expiration: '1 godzina',
          domain: window.location.hostname,
          company: props.companyName,
          privacy: props.privacyPolicyUrl
        }
      ]
    }
  }
])

const showCookieComply = ref(true)
const isModalOpen = ref(false)
const target = ref('body')

const acceptAllLabel = computed(() => ({
  pl: 'Zaakceptuj',
  en: 'Accept'
}))

const preferencesLabel = computed(() => ({
  pl: 'Preferencje',
  en: 'Preferences'
}))

const headerTitle = computed(() => ({
  pl: 'Polityka Ciasteczek',
  en: 'Cookies politics'
}))

const headerDescription = computed(() => ({
  pl: 'Witamy! Stosujemy pliki cookie w celu zapewnienia prawidłowego funkcjonowania serwisu. Możemy również wykorzystywać pliki cookie własne oraz naszych partnerów w celach analitycznych i marketingowych, w szczególności dopasowania treści reklamowych do Twoich preferencji. Korzystanie z analitycznych i marketingowych plików cookie wymaga zgody, którą możesz wyrazić, klikając „Zaakceptuj”. Jeżeli chcesz dostosować swoje zgody dla nas i naszych partnerów, kliknij „Preferencje”. Wyrażoną zgodę możesz wycofać w każdym momencie, zmieniając wybrane ustawienia. <br><br>Korzystanie z plików cookie we wskazanych powyżej celach związane jest z przetwarzaniem Twoich danych osobowych. Administratorem Twoich danych osobowych jest '+ props.companyName +'. W pewnych przypadkach administratorami danych mogą być również nasi partnerzy. Więcej informacji o korzystaniu przez nas i naszych partnerów z plików cookie oraz o przetwarzaniu Twoich danych osobowych, w tym o przysługujących Ci uprawnieniach, znajdziesz w naszej Polityce Cookies.',
  en: 'Welcome! We use cookies to ensure the proper functioning of the website. We may also use our own and our partners cookies for analytical and marketing purposes, in particular matching advertising content to your preferences. The use of analytical and marketing cookies requires consent, which you can give by clicking "Accept". If you want to adjust your consents for us and our partners, click "Preferences". You can withdraw your consent at any time by changing the selected settings. The use of cookies for the purposes indicated above is related to the processing of your personal data. The administrator of your personal data is '+ props.companyName +'. In some cases, our partners may also be data controllers. More information about the use of cookies by us and our partners and about the processing of your personal data, including your rights, can be found in our Cookies Policy.'
}))

// Hook dla inicjalizacji
onMounted(() => {
  const exceptionPaths = [
    '/pl/zasady-uzytkowania-i-pliki-cookies',
    '/en/terms-of-use-and-cookies',
    '/pl/o-cookies',
    '/en/about-cookies'
  ]

  document.getElementById('show-policy')?.addEventListener('click', changePreferences)

  if (localStorage.getItem('cookie-comply') || exceptionPaths.includes(window.location.pathname)) {
    showCookieComply.value = false
  } else {
    window.scrollTo(0, 0)
    document.getElementsByTagName('html')[0].classList.add('overflow-y-hidden');
  }
})

// Metody
const changePreferences = () => {
  window.scrollTo(0, 0)
  document.getElementsByTagName('html')[0].classList.add('overflow-y-hidden');
  openPreferences()
}

const handleAcceptAll = () => {
  showCookieComply.value = false
  document.getElementsByTagName('html')[0].classList.remove('overflow-y-hidden');
  localStorage.setItem('cookie-comply', 'all')
  emit('on-accept-all-cookies')
}

const openPreferences = () => {
  isModalOpen.value = true
}

const onSave = (data: any) => {
  isModalOpen.value = false
  showCookieComply.value = false
  document.getElementsByTagName('html')[0].classList.remove('overflow-y-hidden');

  const preferencesArray = Object.values(data)
  localStorage.setItem('cookie-comply', JSON.stringify(preferencesArray))
  emit('on-save-cookie-preferences', preferencesArray)
}
</script>

<style scoped>

/* Stylizacja */
.cookie-comply__header {
  justify-self: flex-start;
  text-align: initial;
}

.cookie-comply__header-title,
.cookie-comply__header-description {
  margin: 0;
}

.cookie-comply__header-title {
  margin-bottom: var(--spacing-sm);
}

.cookie-comply__header-description {
  line-height: 20px;
}

.cookie-comply__button-accept {
  border: none;
}

.cookie-comply {
  max-height: 90svh;
  max-width: 98svw;
  overflow-y: auto;
}
</style>
